import { useContext, useEffect, useState } from 'react'
import { notifyError } from '../../../../utils/notifications'
import { Button, Empty, Form, Modal, Popover, Space, Table, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import {
  deleteRequest,
  getRequest,
  postRequest,
} from '../../../../services/base'
import { NavContext } from '../../../../context/global_context'
import { useIntl } from 'react-intl'
import InputText from '../../../common/InputText'
import HarnessRelationForm from './HarnessRelationForm'
import RecordsCount from '../../../common/RecordsCount'

const HarnessRelationsModal = ({ harness, open, onClose }) => {
  const intl = useIntl()
  const [, navDispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const [relations, setRelations] = useState([])
  const [filteredRelations, setFilteredRelations] = useState([])
  const [searchText, setSearchText] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const fetchRelations = async () => {
    setLoading(true)
    let res = await getRequest(`/api/harnesses/related_data/${harness?.id}/`)
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'harnesses.relationsLoadError' }))
      setLoading(false)
      return
    }
    setRelations(res?.data || [])
    setFilteredRelations(res?.data || [])
    setLoading(false)
  }

  useEffect(() => {
    if (open && harness?.id) {
      fetchRelations()
      setSelectedRowKeys([])
      setSearchText('')
      setShowForm(false)
    }
  }, [open, harness?.id])

  const handleSearch = (value) => {
    setSearchText(value)
    if (!value) {
      setFilteredRelations(relations)
      return
    }
    const lower = value.toLowerCase()
    setFilteredRelations(
      relations.filter(
        (r) =>
          r.option_code?.name?.toLowerCase().includes(lower) ||
          r.level?.name?.toLowerCase().includes(lower)
      )
    )
  }

  const deleteRelation = (id) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'harnesses.confirmDeleteRelation' }),
      centered: true,
      onOk: async () => {
        let res = await deleteRequest(
          `/api/harnesses/related_data/delete/${id}/`
        )
        if (res?.status) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'harnesses.relationDeleteSuccess' }),
            },
          })
          fetchRelations()
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({ id: 'harnesses.relationDeleteError' }),
            },
          })
        }
      },
    })
  }

  const bulkDelete = () => {
    if (!selectedRowKeys.length) return
    modal.confirm({
      title: intl.formatMessage({ id: 'harnesses.confirmBulkDeleteRelation' }),
      centered: true,
      onOk: async () => {
        const formData = new FormData()
        selectedRowKeys.forEach((id) => formData.append('id', id))

        let res = await postRequest(
          `/api/harnesses/related_data/bulk/delete/`,
          formData
        )
        if (res?.status) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'harnesses.bulkDeleteSuccess' }),
            },
          })
          setSelectedRowKeys([])
          fetchRelations()
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({ id: 'harnesses.bulkDeleteError' }),
            },
          })
        }
      },
    })
  }

  const columns = [
    {
      title: intl.formatMessage({ id: 'harnesses.salesCode' }),
      dataIndex: ['option_code', 'name'],
      sorter: (a, b) =>
        (a.option_code?.name || '').localeCompare(b.option_code?.name || ''),
    },
    {
      title: intl.formatMessage({ id: 'harnesses.partNumber' }),
      dataIndex: ['level', 'name'],
      sorter: (a, b) =>
        (a.level?.name || '').localeCompare(b.level?.name || ''),
    },
    {
      title: intl.formatMessage({ id: 'harnesses.action' }),
      align: 'right',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteRelation(record.id)}
        />
      ),
    },
  ]

  return (
    <>
      <Modal
        open={open}
        title={
          <Space>
            <span>
              {intl.formatMessage({ id: 'harnesses.relationsModalTitle' })}
            </span>
            <Typography.Text code>{harness?.harness_id}</Typography.Text>
          </Space>
        }
        onCancel={onClose}
        width="70%"
        centered
        footer={null}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Toolbar */}
          <Form className="full-width">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <InputText
                name="search_relation"
                type="search"
                isRequired={false}
                placeholder={intl.formatMessage({
                  id: 'harnesses.searchRelation',
                })}
                value={searchText}
                onChange={handleSearch}
                wrapperClass="search-harness-relation"
              />

              <Space size="small">
                {selectedRowKeys.length > 0 && (
                  <Button danger onClick={bulkDelete}>
                    <DeleteOutlined />
                    {intl.formatMessage({ id: 'harnesses.deleteSelected' })}
                  </Button>
                )}
                <Popover
                  open={showForm}
                  onOpenChange={setShowForm}
                  trigger="click"
                  placement="bottomRight"
                  title={intl.formatMessage({ id: 'harnesses.newMapping' })}
                  content={
                    <div style={{ width: 600 }}>
                      <HarnessRelationForm
                        harnessId={harness?.id}
                        onSuccess={() => {
                          setShowForm(false)
                          fetchRelations()
                        }}
                        onClose={() => setShowForm(false)}
                      />
                    </div>
                  }
                >
                  <Button type="primary">
                    {intl.formatMessage({ id: 'harnesses.newMapping' })}
                  </Button>
                </Popover>
              </Space>
            </div>
          </Form>

          {/* Relations table */}
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredRelations}
            loading={loading}
            showSorterTooltip={false}
            pagination={{ 
              pageSize: 10, 
              hideOnSinglePage: true, 
              showSizeChanger: false,
              showTotal: (total) => <RecordsCount count={total} />,
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
            scroll={{ y: '40vh' }}
            locale={{
              emptyText: (
                <div style={{ padding: '40px' }}>
                  <Empty description={<RecordsCount count={0} />} />
                </div>
              ),
            }}
          />
          {filteredRelations &&
            filteredRelations.length <= 10 &&
            filteredRelations.length > 0 && (
              <Typography.Text>
                <RecordsCount count={filteredRelations.length} />
              </Typography.Text>
          )}
        </Space>
      </Modal>
      {contextHolder}
    </>
  )
}

export default HarnessRelationsModal
